import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { collectFormErrors } from '@shared/formHelpers';
import { ExpectedActivitySurvey } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'monthly-spend',
    templateUrl: './monthly-spend.component.html',
    styles: ``
})
export class MonthlySpendComponent implements OnInit {
    private readonly subStepName = 'Expected Monthly Spend';
    private expectedActivitySurveyToSave: ExpectedActivitySurvey;
    monthlySpendOptions = [
        { label: 'UNDER_10000_{currency}', value: 5000 },
        { label: '10000_50000_{currency}', value: 30000 },
        { label: '50000_150000_{currency}', value: 100000 },
        { label: 'OVER_150000_{currency}', value: 150001 }
    ];
    form: UntypedFormGroup;
    loading = false;

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const expectedActivitySurvey = this.route.snapshot.data.expectedActivitySurvey;
        this.expectedActivitySurveyToSave = {
            ...expectedActivitySurvey
        };
        const currency = (this.registrationService.getCountryOfIncorporation() === 'GBR') ? 'GBP' : 'EUR';
        for (const opt of this.monthlySpendOptions) {
            opt.label = opt.label.replace('{currency}', currency);
        }
        this.form = this.fb.group({
            monthlySpend: [expectedActivitySurvey.monthlySpend, Validators.required]
        });
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep() {
        if (this.form.valid) {
            this.loading = true;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            this.expectedActivitySurveyToSave.monthlySpend = this.form.get('monthlySpend').value;
            try {
                await this.registrationService.saveExpectedActivitySurvey(this.expectedActivitySurveyToSave);
                this.trackSubStepCompleted();
                this.registrationService.completeStep(REGISTRATION_STEP_ID.MONTHLY_SPEND);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            } catch (e) {
                this.logger.error('Save expected activity survey failed', e, 'with expected activity survey', this.expectedActivitySurveyToSave);
                this.loading = false;
                this.dynatrace.leaveAction(submitActionId);
            }
        } else {
            this.form.markAllAsTouched();
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
        }
    }


}
